/* ─────────────────────────────────────────────────────────
 * PRODUCT IMAGE PREVIEW STORYBOARD
 *
 *    0ms   selected card image becomes the spatial origin
 *  340ms   full-resolution image settles in the viewport
 *    Esc   preview reverses toward its originating card
 *  260ms   focus returns to the expand control
 * ───────────────────────────────────────────────────────── */
const PREVIEW_MOTION = Object.freeze({
  openDuration: 340,
  closeDuration: 260,
  openEasing: 'cubic-bezier(0.22, 1, 0.36, 1)',
  closeEasing: 'cubic-bezier(0.4, 0, 0.2, 1)',
});

const dialog = document.querySelector('[data-product-discovery-image-dialog]');
const card = dialog?.querySelector('[data-product-discovery-image-dialog-card]');
const backdrop = dialog?.querySelector('[data-product-discovery-image-dialog-dismiss]');
const closeButton = dialog?.querySelector('[data-product-discovery-image-dialog-close]');
const image = dialog?.querySelector('[data-product-discovery-image-dialog-image]');
const title = dialog?.querySelector('[data-product-discovery-image-dialog-title]');

if (
  dialog &&
  card &&
  backdrop &&
  closeButton &&
  image &&
  title &&
  typeof dialog.showModal === 'function' &&
  typeof card.animate === 'function'
) {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let activeTrigger = null;
  let activeSource = null;
  let state = 'closed';
  let sequence = 0;
  let animations = [];

  const buildResponsiveSourceSet = (source) => {
    try {
      const url = new URL(source, document.baseURI);

      if (!url.searchParams.has('width')) {
        return '';
      }

      return [740, 1200, 1600, 2000]
        .map((width) => {
          const candidate = new URL(url);
          candidate.searchParams.set('width', String(width));
          return `${candidate.href} ${width}w`;
        })
        .join(', ');
    } catch {
      return '';
    }
  };

  const cancelAnimations = () => {
    animations.forEach((animation) => animation.cancel());
    animations = [];
  };

  const animateBackdrop = (keyframes, timing) => {
    try {
      return dialog.animate(keyframes, { ...timing, pseudoElement: '::backdrop' });
    } catch {
      // Older engines still get the native, full-viewport backdrop without a fade.
      return backdrop.animate(keyframes, timing);
    }
  };

  const getSharedTransform = (from, to) => {
    const translateX = from.left - to.left;
    const translateY = from.top - to.top;
    const scaleX = from.width / to.width;
    const scaleY = from.height / to.height;

    return `translate3d(${translateX}px, ${translateY}px, 0) scale(${scaleX}, ${scaleY})`;
  };

  const finishOpen = (expectedSequence) => {
    if (sequence !== expectedSequence || state !== 'opening') {
      return;
    }

    state = 'open';
    cancelAnimations();
    delete dialog.dataset.mediaDialogState;
  };

  const finishClose = (expectedSequence) => {
    if (sequence !== expectedSequence) {
      return;
    }

    state = 'closed';
    cancelAnimations();
    delete dialog.dataset.mediaDialogState;
    activeTrigger?.setAttribute('aria-expanded', 'false');

    if (dialog.open) {
      dialog.close();
    }

    if (activeTrigger?.getClientRects().length) {
      activeTrigger.focus({ preventScroll: true });
    }

    activeTrigger = null;
    activeSource = null;
    closeButton.removeAttribute('data-suppress-focus-ring');
  };

  const loadExpandedImage = (trigger, expectedSequence) => {
    const sourceImage = activeSource?.querySelector('img');
    const previewSource = sourceImage?.currentSrc || sourceImage?.src;
    const expandedSource = trigger.dataset.productDiscoveryImageSrc || previewSource;
    const imageTitle = trigger.dataset.productDiscoveryImageTitle || '';

    title.textContent = imageTitle ? `Expanded image of ${imageTitle}` : 'Expanded coffee image';
    image.alt = imageTitle;

    if (previewSource) {
      image.src = previewSource;
    }

    if (!expandedSource || expandedSource === previewSource) {
      return;
    }

    const preload = new Image();
    const responsiveSourceSet = buildResponsiveSourceSet(expandedSource);
    preload.decoding = 'async';
    preload.sizes = '(min-width: 1024px) 900px, calc(100vw - 32px)';
    if (responsiveSourceSet) {
      preload.srcset = responsiveSourceSet;
    }
    preload.src = expandedSource;
    preload
      .decode()
      .catch(() => {})
      .then(() => {
        if (sequence === expectedSequence && state !== 'closed' && preload.naturalWidth) {
          image.src = preload.currentSrc || expandedSource;
        }
      });
  };

  const openDialog = (trigger, event) => {
    if (state !== 'closed') {
      return;
    }

    activeTrigger = trigger;
    activeSource = trigger.closest(
      '[data-product-discovery-image-source], .cart-empty__product-image-frame',
    );

    if (!activeSource) {
      activeTrigger = null;
      return;
    }

    state = 'opening';
    const expectedSequence = ++sequence;
    const sourceRect = activeSource.getBoundingClientRect();
    const sourceRadius = getComputedStyle(activeSource).borderRadius;

    trigger.setAttribute('aria-expanded', 'true');
    card.classList.toggle(
      'product-image-preview__card--contain',
      trigger.dataset.productDiscoveryImageFit === 'contain',
    );
    const previewAspectRatio = Number.parseFloat(
      trigger.dataset.productDiscoveryImageAspectRatio || '',
    );
    if (Number.isFinite(previewAspectRatio) && previewAspectRatio > 0) {
      card.style.setProperty('--product-image-preview-aspect-ratio', String(previewAspectRatio));
    } else {
      card.style.removeProperty('--product-image-preview-aspect-ratio');
    }
    dialog.showModal();
    dialog.dataset.mediaDialogState = 'opening';
    image.loading = 'eager';
    loadExpandedImage(trigger, expectedSequence);
    if (event?.detail > 0) {
      closeButton.setAttribute('data-suppress-focus-ring', '');
    }
    closeButton.focus({ preventScroll: true });

    if (reducedMotion.matches) {
      finishOpen(expectedSequence);
      return;
    }

    const cardRect = card.getBoundingClientRect();
    const dialogRadius = getComputedStyle(card).borderRadius;
    const timing = {
      duration: PREVIEW_MOTION.openDuration,
      easing: PREVIEW_MOTION.openEasing,
      fill: 'both',
    };

    animations = [
      card.animate(
        [
          { borderRadius: sourceRadius, transform: getSharedTransform(sourceRect, cardRect) },
          { borderRadius: dialogRadius, transform: 'translate3d(0, 0, 0) scale(1, 1)' },
        ],
        timing,
      ),
      animateBackdrop([{ opacity: 0 }, { opacity: 1 }], timing),
    ];

    Promise.allSettled(animations.map((animation) => animation.finished)).then(() =>
      finishOpen(expectedSequence),
    );
  };

  const closeDialog = () => {
    if (state === 'closed' || state === 'closing') {
      return;
    }

    state = 'closing';
    const expectedSequence = ++sequence;
    dialog.dataset.mediaDialogState = 'closing';

    if (reducedMotion.matches || !activeSource?.getClientRects().length) {
      finishClose(expectedSequence);
      return;
    }

    cancelAnimations();
    const sourceRect = activeSource.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const sourceRadius = getComputedStyle(activeSource).borderRadius;
    const dialogRadius = getComputedStyle(card).borderRadius;
    const timing = {
      duration: PREVIEW_MOTION.closeDuration,
      easing: PREVIEW_MOTION.closeEasing,
      fill: 'both',
    };

    animations = [
      card.animate(
        [
          { borderRadius: dialogRadius, transform: 'translate3d(0, 0, 0) scale(1, 1)' },
          { borderRadius: sourceRadius, transform: getSharedTransform(sourceRect, cardRect) },
        ],
        timing,
      ),
      animateBackdrop([{ opacity: 1 }, { opacity: 0 }], timing),
    ];

    Promise.allSettled(animations.map((animation) => animation.finished)).then(() =>
      finishClose(expectedSequence),
    );
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const trigger = target?.closest('[data-product-discovery-image-open]');

    if (trigger) {
      event.preventDefault();
      openDialog(trigger, event);
    }
  });
  closeButton.addEventListener('click', closeDialog);
  backdrop.addEventListener('click', closeDialog);
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog || event.target === backdrop) {
      closeDialog();
    }
  });
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    closeDialog();
  });
  dialog.addEventListener('keydown', () => {
    closeButton.removeAttribute('data-suppress-focus-ring');
  });
  dialog.addEventListener('close', () => {
    if (state !== 'closed') {
      const expectedSequence = ++sequence;
      finishClose(expectedSequence);
    }
  });
}
